import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import SearchSoundEffects from '@App/api/query/SearchSoundEffects.graphql';
import Query from '@App/components/api/Query';
import SoundEffectList from '@App/components/model/SoundEffectList';
import { SoundEffectPropType, SoundEffectData } from '@App/components/model/SoundEffectPropType';
import BlockTitle from '@App/components/layout/BlockTitle';
import Button from '@App/components/ui/Button';
import Loader from '@App/components/ui/Loader';

interface ResultListProps {
  count: number
  hasNextPage: boolean
  sounds: SoundEffectData[]
  onLoadMore: () => Promise<unknown>
  loading: boolean
  /** False when neither a term nor a category is set: the list is the default catalogue selection. */
  searching: boolean
}

ResultList.propTypes = {
  count: PropTypes.number.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  sounds: PropTypes.arrayOf(SoundEffectPropType.isRequired).isRequired,
  onLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  searching: PropTypes.bool.isRequired,
};

function ResultList({ loading, sounds, count, hasNextPage, onLoadMore, searching }: ResultListProps) {
  // This block reports on a search, and on nothing else: the title of the page is the header's
  // business now (see SoundEffects.PageHeader). With no term and no category there is no search to
  // report — counting the whole catalogue ("119 497 effets sonores trouvés") would say nothing, and
  // a standing "Une sélection d'effets sonores" would only repeat, weaker, the title just above it.
  const title = searching
    ? (loading ? I18n.t('results.loading') : I18n.t('soundEffects.results.title', { count }))
    : null;
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const onClick = () => {
    setLoadingMore(true);
    onLoadMore().finally(() => setLoadingMore(false));
  };

  return <Fragment>
    {/* h2: the page carries its own h1 in the header above (see SoundEffects.PageHeader). */}
    {title === null ? null : <BlockTitle title={title} level="h2" />}
    {loading ? <div><Loader /></div> : <SoundEffectList sounds={sounds} />}
    {hasNextPage ? <Button loading={loadingMore} onClick={onClick} label={I18n.t('results.more')} /> : null}
  </Fragment>;
}

function mapResult(data) {
  return {
    hasNextPage: data.searchSoundEffects.pageInfo.hasNextPage,
    count: data.searchSoundEffects.totalCount,
    sounds: data.searchSoundEffects.edges.map(edge => edge.node),
    loading: false,
  };
}

function mapLoading() {
  return {
    hasNextPage: false,
    count: 0,
    sounds: [],
    loading: true,
  };
}

interface SoundEffectResultListProps {
  search: string
  catId?: string | null
  limit?: number
}

export default function SoundEffectResultList({ search, catId, limit }: SoundEffectResultListProps) {
  // Sound-effect preview URLs are signed and short-lived (server contract: not cacheable). Use
  // cache-and-network so a revisit refetches fresh URLs instead of replaying stale, already-expired
  // ones from Apollo's normalized cache; the player's audio-error recovery (Player.onError) covers
  // the residual case of a URL that expires while the page stays open.
  return (
    <Query
      component={ResultList}
      loadingComponent={ResultList}
      query={SearchSoundEffects}
      variables={{ search, catId, limit }}
      mapResult={mapResult}
      mapLoading={mapLoading}
      childProps={{ searching: search !== '' || Boolean(catId) }}
      loadMore="searchSoundEffects"
      fetchPolicy="cache-and-network"
    />
  );
}

SoundEffectResultList.propTypes = {
  search: PropTypes.string.isRequired,
  catId: PropTypes.string,
  limit: PropTypes.number,
};

SoundEffectResultList.defaultProps = {
  catId: null,
  limit: 30,
};
